import type { VFC } from 'react'
import { Button, StatusDot, Timestamp, renderIcon } from '@opallabs/kuiper'
import type { WithRequiredTheme } from '@opallabs/lapidary'
import * as styles from './styles'
import { NotificationProps } from './types'

const statusState = (
  seen: boolean,
  read: boolean,
  placeholder: boolean
): string => {
  if (placeholder) {
    return 'placeholder'
  }
  return seen && !read ? 'inactive' : 'warning'
}

const NotificationPlaceholder: VFC<WithRequiredTheme> = ({ theme }) => (
  <div css={styles.placeHolderRow}>
    <div css={styles.placeholderDiv({ theme })} />
    <div css={styles.placeholderDiv({ theme })} />
    <div css={styles.placeholderDiv({ theme })} />
  </div>
)
NotificationPlaceholder.displayName =
  'Satellite: NotificationPlaceholder ← NotificationItem'

export const NotificationItem: React.FC<
  React.PropsWithChildren<NotificationProps>
> = ({
  children,
  downloadUrl,
  filename,
  id,
  onItemClick,
  onRemove,
  placeholder,
  read,
  seen,
  theme = 'light',
  timeFormat,
  timestamp,
}) => {
  const initiateDownload = (downloadUrl: string) => {
    if (filename?.endsWith('.zip')) {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = downloadUrl
      document.body.appendChild(iframe)

      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 2000)
    } else {
      window.open(downloadUrl)
    }
  }

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    if (onRemove) {
      onRemove(id)
    }
  }

  const shouldShowCloseButton = !placeholder && !downloadUrl && !!onRemove

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onItemClick}
      css={
        seen || read || placeholder
          ? styles.notificationItemStyles({
              placeholder,
              theme,
            })
          : [
              styles.notificationItemStyles({
                placeholder,
                theme,
              }),
              styles.newNotifStyles({ theme }),
              styles.newNotifDelay(),
            ]
      }
      key={`notification-${id}`}
    >
      <div css={styles.headerStyles}>
        {!(seen && read) && (
          <div role="status" css={styles.statusStyles}>
            <StatusDot status={statusState(seen, read, placeholder)} />
          </div>
        )}
        <div css={styles.notificationContentStyles}>
          {placeholder ? <NotificationPlaceholder theme={theme} /> : children}
        </div>
        {shouldShowCloseButton && (
          <button
            type="button"
            className="close-button"
            css={styles.closeButtonStyles({ theme })}
            onClick={handleRemove}
            aria-label="Remove notification"
          >
            {renderIcon('close-sm', { height: 8, width: 8 })}
          </button>
        )}
      </div>
      {downloadUrl && downloadUrl.length && (
        <div>
          <Button
            label="download"
            onClick={() => initiateDownload(downloadUrl)}
            size="small"
            type="primary"
          />
        </div>
      )}
      {placeholder ? (
        <div css={[styles.placeholderDiv({ theme }), { marginTop: '8px' }]} />
      ) : (
        <Timestamp format={timeFormat} theme={theme} timestamp={timestamp} />
      )}
    </div>
  )
}
NotificationItem.displayName = 'Satellite: NotificationItem'
